import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  User, LogOut, FileText, CreditCard, Settings, Home, Download,
  Wifi, Calendar, CheckCircle, Clock, AlertCircle, ChevronRight, RotateCcw, Wrench,
  Upload, Camera, Activity, Signal, ShieldCheck, ArrowUpDown, Eye, EyeOff
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import ktcLogo from '../assets/ktc.png';
import SignalHistoryChart from '../components/SignalHistoryChart';
import StaffPageLoadingOverlay from '../components/StaffPageLoadingOverlay';
import { clearCustomerSession, requestCustomerLogout, resolveBackendBaseUrl } from '../utils/runtimeConfig';

const API = `${resolveBackendBaseUrl()}/api`;
const BASE_URL = `${resolveBackendBaseUrl()}`.replace(/\/$/, '');

const CustomerPortal = () => {
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [billings, setBillings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [onuStatus, setOnuStatus] = useState(null);
  const [pppoeStatus, setPppoeStatus] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [wifiForm, setWifiForm] = useState({ ssid_name: '', ssid_password: '', confirm_password: '' });
  const [wifiErrors, setWifiErrors] = useState({});
  const [wifiSaving, setWifiSaving] = useState(false);
  const [wifiConfirmOpen, setWifiConfirmOpen] = useState(false);
  const [wifiRequestStatus, setWifiRequestStatus] = useState({ state: 'idle', requestId: null, message: '' });
  const [wifiPolling, setWifiPolling] = useState(false);
  const [showWifiPassword, setShowWifiPassword] = useState(false);
  const [restartingOnu, setRestartingOnu] = useState(false);
  const [restartConfirmOpen, setRestartConfirmOpen] = useState(false);
  const [restartCooldownUntil, setRestartCooldownUntil] = useState(0);
  const [clockNow, setClockNow] = useState(() => Date.now());
  const [tickets, setTickets] = useState([]);
  const [ticketForm, setTicketForm] = useState({ issue_notes: '', category: 'lainnya' });
  const [ticketPhotos, setTicketPhotos] = useState([]);
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [paymentOptions, setPaymentOptions] = useState({ transfer_enabled: true, gateway_enabled: true, bank_accounts: [], providers: [] });
  const [gatewayTransactions, setGatewayTransactions] = useState([]);
  const [creatingGatewayForBillingId, setCreatingGatewayForBillingId] = useState('');
  const [selectedGatewayProvider, setSelectedGatewayProvider] = useState('xendit');
  const [transferSubmissions, setTransferSubmissions] = useState([]);
  const [whatsAppChatLink, setWhatsAppChatLink] = useState(null);
  const [selectedTransferBankByBilling, setSelectedTransferBankByBilling] = useState({});
  const [transferNoteByBilling, setTransferNoteByBilling] = useState({});
  const [transferProofFileByBilling, setTransferProofFileByBilling] = useState({});
  const [submittingTransferForBilling, setSubmittingTransferForBilling] = useState('');

  const sortedBillings = [...billings].sort((a, b) => {
    const aTime = new Date(a?.created_at || a?.due_date || 0).getTime();
    const bTime = new Date(b?.created_at || b?.due_date || 0).getTime();
    return bTime - aTime;
  });

  const latestBilling = sortedBillings[0] || null;

  const wifiReadiness = (() => {
    const online = onuStatus?.connection_status === 'online';
    const acsReady = Boolean(onuStatus?.genieacs_connected);
    const unsupportedModel = onuStatus?.wifi_unsupported === true
      || onuStatus?.wifi_edit_supported === false
      || onuStatus?.model_supported === false;
    const reasons = [];
    if (!online) reasons.push('ONU sedang offline');
    if (!acsReady) reasons.push('ACS belum siap');
    if (unsupportedModel) reasons.push('Model ONU belum mendukung ubah WiFi mandiri');
    return {
      online,
      acsReady,
      unsupportedModel,
      ready: reasons.length === 0,
      reasons,
      lastSyncAt: onuStatus?.last_sync_at || onuStatus?.updated_at || null,
      lastSeenAt: onuStatus?.last_seen_at || onuStatus?.last_seen || null,
    };
  })();

  const restartCooldownLeft = Math.max(0, Math.ceil((restartCooldownUntil - clockNow) / 1000));

  const logPortalEvent = useCallback((eventName, payload = {}) => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('portal-audit-event', {
        detail: { eventName, at: new Date().toISOString(), ...payload },
      }));
    }
  }, []);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('ktc_customer_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const handleLogout = useCallback(async () => {
    await requestCustomerLogout();
    clearCustomerSession();
    navigate('/portal/login');
  }, [navigate]);

  const fetchData = useCallback(async () => {
    try {
      const headers = getHeaders();

      // Fetch profile
      const profileRes = await axios.get(`${API}/portal/me`, { headers });
      setCustomer(profileRes.data);
      localStorage.setItem('ktc_customer', JSON.stringify(profileRes.data));

      // Fetch billings
      const billingsRes = await axios.get(`${API}/portal/billings`, { headers });
      setBillings(billingsRes.data.billings || []);
      setSummary(billingsRes.data.summary);

      const [onuRes, wifiStatusRes, historyRes, ticketsRes, paymentOptionsRes, gatewayTxRes, transferSubRes, waChatRes, pppoeRes] = await Promise.all([
        axios.get(`${API}/portal/onu-status`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/portal/wifi-status`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/portal/onu-status-history`, { headers, params: { limit: 48 } }).catch(() => ({ data: { history: [] } })),
        axios.get(`${API}/portal/tickets`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/portal/payment-options`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/portal/payments/gateway`, { headers, params: { limit: 20 } }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/portal/payments/transfer-submissions`, { headers, params: { limit: 20 } }).catch(() => ({ data: { items: [] } })),
        axios.get(`${API}/portal/whatsapp/chat-link`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/portal/pppoe-status`, { headers }).catch(() => ({ data: null })),
      ]);
      const mergedOnuStatus = (onuRes.data || wifiStatusRes.data)
        ? { ...(onuRes.data || {}), ...(wifiStatusRes.data || {}) }
        : null;
      setOnuStatus(mergedOnuStatus);
      setStatusHistory(Array.isArray(historyRes.data?.history) ? historyRes.data.history : []);
      setTickets(Array.isArray(ticketsRes.data) ? ticketsRes.data : []);
      const paymentPayload = paymentOptionsRes.data && typeof paymentOptionsRes.data === 'object' ? paymentOptionsRes.data : {};
      setPaymentOptions({
        transfer_enabled: Boolean(paymentPayload.transfer_enabled ?? true),
        gateway_enabled: Boolean(paymentPayload.gateway_enabled ?? true),
        bank_accounts: Array.isArray(paymentPayload.bank_accounts) ? paymentPayload.bank_accounts : [],
        providers: Array.isArray(paymentPayload.providers) ? paymentPayload.providers : [],
      });
      setSelectedGatewayProvider(String(paymentPayload.default_provider || paymentPayload.providers?.[0] || 'xendit'));
      setGatewayTransactions(Array.isArray(gatewayTxRes.data?.items) ? gatewayTxRes.data.items : []);
      setTransferSubmissions(Array.isArray(transferSubRes.data?.items) ? transferSubRes.data.items : []);
      setWhatsAppChatLink(waChatRes?.data && typeof waChatRes.data === 'object' ? waChatRes.data : null);
      setPppoeStatus(pppoeRes?.data && typeof pppoeRes.data === 'object' ? pppoeRes.data : null);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Sesi habis, silakan login kembali');
        void handleLogout();
      }
      setStatusHistory([]);
    } finally {
      setLoading(false);
    }
  }, [getHeaders, handleLogout]);

  useEffect(() => {
    const storedCustomer = localStorage.getItem('ktc_customer');
    if (storedCustomer) {
      setCustomer(JSON.parse(storedCustomer));
    }
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!onuStatus) return;
    setWifiForm({
      ssid_name: onuStatus.ssid_name || '',
      ssid_password: '',
      confirm_password: '',
    });
  }, [onuStatus]);

  useEffect(() => {
    const timer = window.setInterval(() => setClockNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const handleDownloadPDF = async (billingId, invoiceNumber) => {
    try {
      const response = await axios.get(`${API}/portal/billings/${billingId}/pdf`, {
        headers: getHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Invoice berhasil didownload');
    } catch (error) {
      toast.error('Gagal download invoice');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `Rp ${amount?.toLocaleString('id-ID')}`;
  };

  const getOnuConnectionText = () => {
    if (!onuStatus) return 'Belum sinkron';
    if (onuStatus.connection_status === 'online') return 'Terkoneksi';
    if (onuStatus.connection_status === 'offline') return 'Offline';
    if (onuStatus.connection_status === 'not_mapped') return 'Belum Mapping';
    return 'Tidak Tersedia';
  };

  const getOnuConnectionClass = () => {
    if (!onuStatus) return 'text-slate-500';
    if (onuStatus.connection_status === 'online') return 'text-green-600';
    if (onuStatus.connection_status === 'offline') return 'text-red-600';
    if (onuStatus.connection_status === 'not_mapped') return 'text-yellow-600';
    return 'text-slate-500';
  };

  const getGenieacsText = () => {
    if (!onuStatus) return 'Belum sinkron';
    if (onuStatus.genieacs_connected) return 'Terhubung';
    if (onuStatus.genieacs_configured) return 'Tidak terhubung';
    return 'Belum dikonfigurasi';
  };

  const getGenieacsClass = () => {
    if (!onuStatus) return 'text-slate-500';
    if (onuStatus.genieacs_connected) return 'text-green-600';
    if (onuStatus.genieacs_configured) return 'text-red-600';
    return 'text-yellow-600';
  };

  const getRedamanText = () => {
    if (!onuStatus) return 'Redaman tidak tersedia';
    if (Number.isFinite(onuStatus.attenuation_db)) return `Redaman: ${onuStatus.attenuation_db.toFixed(2)} dB`;
    if (Number.isFinite(onuStatus.rx_power_dbm)) return `Redaman (RX Power): ${onuStatus.rx_power_dbm.toFixed(2)} dBm`;
    return 'Redaman tidak tersedia';
  };

  const getRedamanClass = () => {
    const attenuation = onuStatus?.attenuation_db;
    const rxPower = onuStatus?.rx_power_dbm;
    const metric = Number.isFinite(attenuation) ? attenuation : (Number.isFinite(rxPower) ? Math.abs(rxPower) : null);
    if (!Number.isFinite(metric)) return 'text-slate-500';
    if (metric < 21) return 'text-green-600';
    if (metric <= 25) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPppoeStatusText = () => {
    if (!pppoeStatus) return 'Tidak tersedia';
    const s = String(pppoeStatus.connection_status || pppoeStatus.status || '').toLowerCase();
    if (s === 'online') return 'Online';
    if (s === 'offline') return 'Offline';
    return 'Tidak tersedia';
  };
  const getPppoeStatusClass = () => {
    if (!pppoeStatus) return 'text-slate-500';
    const s = String(pppoeStatus.connection_status || pppoeStatus.status || '').toLowerCase();
    if (s === 'online') return 'text-green-600';
    if (s === 'offline') return 'text-red-600';
    return 'text-slate-500';
  };
  const getConnectionHealthLevel = () => {
    const onuOnline = onuStatus?.connection_status === 'online';
    const pppoeOnline = String(pppoeStatus?.connection_status || pppoeStatus?.status || '').toLowerCase() === 'online';
    if (onuOnline && pppoeOnline) return 'healthy';
    if (onuOnline || pppoeOnline) return 'degraded';
    if (onuStatus?.connection_status === 'not_mapped') return 'unmapped';
    return 'offline';
  };
  const healthLevel = getConnectionHealthLevel();
  const healthConfig = {
    healthy: { color: 'bg-emerald-500', ring: 'ring-emerald-500/30', text: 'Koneksi Sehat', textClass: 'text-emerald-400' },
    degraded: { color: 'bg-amber-500', ring: 'ring-amber-500/30', text: 'Koneksi Terganggu', textClass: 'text-amber-400' },
    unmapped: { color: 'bg-slate-500', ring: 'ring-slate-500/30', text: 'Belum Mapping', textClass: 'text-slate-400' },
    offline: { color: 'bg-red-500', ring: 'ring-red-500/30', text: 'Offline', textClass: 'text-red-400' },
  }[healthLevel];
  const autoDiagnosis = (() => {
    const fromBackend = onuStatus?.auto_diagnosis;
    if (fromBackend && typeof fromBackend === 'object' && fromBackend.category) {
      return {
        category: String(fromBackend.category),
        reason: String(fromBackend.reason || 'Diagnosis otomatis tersedia.'),
      };
    }

    const billingState = String(customer?.billing_state || '').toLowerCase();
    if (['overdue', 'suspend_candidate', 'suspended'].includes(billingState)) {
      return { category: 'billing_issue', reason: 'Status tagihan perlu ditindaklanjuti agar layanan tetap stabil.' };
    }
    if (String(onuStatus?.connection_status || '').toLowerCase() !== 'online') {
      return { category: 'network_issue', reason: 'ONU sedang offline. Coba cek koneksi listrik dan kabel optik.' };
    }
    if (Number.isFinite(onuStatus?.rx_power_dbm) && onuStatus.rx_power_dbm <= -24) {
      return { category: 'network_issue', reason: 'Sinyal optik terdeteksi lemah, kemungkinan gangguan jalur.' };
    }
    const hasActiveTicket = Array.isArray(tickets) && tickets.some((item) => ['pending_approval', 'approved', 'in_progress', 'needs_special_handling'].includes(String(item?.status || '').toLowerCase()));
    if (hasActiveTicket) {
      return { category: 'wifi_likely_issue', reason: 'Ada tiket aktif, indikasi gangguan lokal (WiFi/perangkat) masih ditangani.' };
    }
    return { category: 'healthy', reason: 'Semua indikator utama normal.' };
  })();
  const diagnosisConfig = {
    healthy: { label: 'Healthy', chip: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30' },
    wifi_likely_issue: { label: 'WiFi Likely Issue', chip: 'text-amber-300 bg-amber-500/10 border-amber-500/30' },
    network_issue: { label: 'Network Issue', chip: 'text-orange-300 bg-orange-500/10 border-orange-500/30' },
    billing_issue: { label: 'Billing Issue', chip: 'text-red-300 bg-red-500/10 border-red-500/30' },
  }[autoDiagnosis.category] || { label: 'Unknown', chip: 'text-slate-300 bg-slate-500/10 border-slate-500/30' };

  const getTicketStatusLabel = (status) => {
    if (status === 'pending_approval') return 'Menunggu Approval';
    if (status === 'approved' || status === 'in_progress') return 'Sedang Ditangani';
    if (status === 'completed') return 'Selesai';
    if (status === 'needs_special_handling') return 'Butuh Penanganan Khusus';
    return 'Tidak diketahui';
  };

  const getTicketStatusClass = (status) => {
    if (status === 'completed') return 'text-green-600';
    if (status === 'needs_special_handling') return 'text-red-600';
    if (status === 'approved' || status === 'in_progress') return 'text-cyan-600';
    if (status === 'pending_approval') return 'text-yellow-600';
    return 'text-slate-500';
  };

  const handleCreateTicket = async (event) => {
    event.preventDefault();
    const issueNotes = ticketForm.issue_notes.trim();
    if (issueNotes.length < 5) {
      toast.error('Keluhan minimal 5 karakter');
      return;
    }
    setTicketSubmitting(true);
    try {
      const form = new FormData();
      form.append('issue_notes', issueNotes);
      form.append('category', ticketForm.category || 'lainnya');
      form.append('onu_online', String(onuStatus?.connection_status === 'online'));
      if (Number.isFinite(onuStatus?.rx_power_dbm)) form.append('rx_dbm', String(onuStatus.rx_power_dbm));
      if (Number.isFinite(onuStatus?.total_stations)) form.append('connected_devices', String(onuStatus.total_stations));
      if (onuStatus?.last_seen || onuStatus?.updated_at) form.append('last_seen', String(onuStatus.last_seen || onuStatus.updated_at));
      ticketPhotos.forEach((file) => form.append('photos', file));
      const response = await axios.post(`${API}/portal/tickets`, form, { headers: getHeaders() });
      setTickets((prev) => [response.data, ...prev]);
      setTicketForm({ issue_notes: '', category: 'lainnya' });
      setTicketPhotos([]);
      logPortalEvent('portal_ticket_created', { category: ticketForm.category || 'lainnya' });
      toast.success('Tiket gangguan berhasil dibuat');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal membuat tiket gangguan');
    } finally {
      setTicketSubmitting(false);
    }
  };

  const validateWifiForm = useCallback(() => {
    const ssidName = wifiForm.ssid_name.trim();
    const ssidPassword = wifiForm.ssid_password.trim();
    const confirmPassword = wifiForm.confirm_password.trim();
    const nextErrors = {};
    if (!ssidName) nextErrors.ssid_name = 'SSID wajib diisi';
    else if (ssidName.length > 32) nextErrors.ssid_name = 'SSID maksimal 32 karakter';
    if (!ssidPassword) nextErrors.ssid_password = 'Password wajib diisi';
    else if (ssidPassword.length < 8) nextErrors.ssid_password = 'Password minimal 8 karakter';
    else if (ssidPassword.length > 63) nextErrors.ssid_password = 'Password maksimal 63 karakter';
    if (confirmPassword !== ssidPassword) nextErrors.confirm_password = 'Konfirmasi password tidak sama';
    setWifiErrors(nextErrors);
    return { valid: Object.keys(nextErrors).length === 0, ssidName, ssidPassword };
  }, [wifiForm]);

  const fetchWifiUpdateResult = useCallback(async (requestId) => {
    if (!requestId) return null;
    try {
      const headers = getHeaders();
      const byPath = await axios.get(`${API}/portal/update-wifi/${requestId}`, { headers });
      return byPath.data;
    } catch (error) {
      if (error.response?.status === 404 || error.response?.status === 405) {
        try {
          const headers = getHeaders();
          const byQuery = await axios.get(`${API}/portal/update-wifi-result`, {
            headers,
            params: { request_id: requestId },
          });
          return byQuery.data;
        } catch {
          return null;
        }
      }
      return null;
    }
  }, [getHeaders]);

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleSaveWifi = async () => {
    if (!wifiReadiness.ready) {
      toast.error(wifiReadiness.reasons[0] || 'WiFi ONU belum siap diubah');
      return;
    }
    const { valid, ssidName, ssidPassword } = validateWifiForm();
    if (!valid) {
      toast.error('Periksa kembali form WiFi');
      return;
    }

    setWifiConfirmOpen(false);

    setWifiSaving(true);
    setWifiPolling(false);
    setWifiRequestStatus({ state: 'loading', requestId: null, message: 'Mengirim perubahan WiFi ke ONU...' });
    try {
      const headers = getHeaders();
      let response;
      try {
        response = await axios.post(`${API}/portal/update-wifi`, {
          ssid_name: ssidName,
          ssid_password: ssidPassword,
        }, { headers });
      } catch (error) {
        if (error.response?.status !== 404 && error.response?.status !== 405) {
          throw error;
        }
        response = await axios.put(`${API}/portal/wifi-settings`, {
          ssid_name: ssidName,
          ssid_password: ssidPassword,
        }, { headers });
      }

      const requestId = response.data?.request_id || response.data?.id || null;
      const backendState = String(response.data?.status || '').toLowerCase();
      const immediateSuccess = backendState === 'success' || backendState === 'done';
      const immediateFailed = backendState === 'failed' || backendState === 'error';
      const pendingConfirmation = backendState === 'pending_confirmation' || backendState === 'awaiting_confirmation';

      if (immediateFailed) {
        setWifiRequestStatus({ state: 'failed', requestId, message: response.data?.message || 'Permintaan WiFi gagal diproses' });
        toast.error(response.data?.message || 'Perubahan WiFi gagal diproses');
      } else if (pendingConfirmation) {
        setWifiRequestStatus({
          state: 'pending_confirmation',
          requestId,
          message: response.data?.message || 'Perubahan menunggu konfirmasi perangkat.',
        });
        toast.message('Perubahan WiFi menunggu konfirmasi perangkat');
      } else if (requestId && !immediateSuccess) {
        setWifiPolling(true);
        setWifiRequestStatus({ state: 'queued', requestId, message: 'Perubahan masuk antrean perangkat ONU...' });
        toast.message('Perubahan WiFi masuk antrean');
        for (let i = 0; i < 6; i += 1) {
          await wait(2000);
          const result = await fetchWifiUpdateResult(requestId);
          const resultState = String(result?.status || '').toLowerCase();
          if (resultState === 'success' || resultState === 'done') {
            setWifiRequestStatus({ state: 'success', requestId, message: result?.message || 'Perubahan WiFi berhasil diterapkan' });
            toast.success(result?.message || 'Perubahan WiFi berhasil diterapkan');
            logPortalEvent('portal_wifi_updated', { request_id: requestId, mode: 'async' });
            break;
          }
          if (resultState === 'pending_confirmation' || resultState === 'awaiting_confirmation') {
            setWifiRequestStatus({
              state: 'pending_confirmation',
              requestId,
              message: result?.message || 'Perubahan menunggu konfirmasi perangkat.',
            });
          }
          if (resultState === 'failed' || resultState === 'error') {
            setWifiRequestStatus({ state: 'failed', requestId, message: result?.message || 'Perubahan WiFi gagal diterapkan' });
            toast.error(result?.message || 'Perubahan WiFi gagal diterapkan');
            break;
          }
        }
        setWifiPolling(false);
      } else {
        setWifiRequestStatus({ state: 'success', requestId, message: 'Perubahan WiFi berhasil dikirim ke ONU' });
        toast.success('Perubahan WiFi berhasil dikirim ke ONU');
        logPortalEvent('portal_wifi_updated', { request_id: requestId, mode: 'sync' });
      }
      await fetchData();
      setWifiForm((prev) => ({ ...prev, ssid_password: '', confirm_password: '' }));
    } catch (error) {
      setWifiRequestStatus({ state: 'failed', requestId: null, message: error.response?.data?.detail || 'Gagal memperbarui WiFi ONU' });
      logPortalEvent('portal_wifi_update_failed', { reason: error.response?.data?.detail || 'request_failed' });
      toast.error(error.response?.data?.detail || 'Gagal memperbarui WiFi ONU');
    } finally {
      setWifiSaving(false);
    }
  };

  const handleRestartOnu = async () => {
    if (restartCooldownLeft > 0) {
      toast.error(`Tunggu ${restartCooldownLeft} detik sebelum restart ulang`);
      return;
    }
    setRestartConfirmOpen(false);
    setRestartingOnu(true);
    try {
      await axios.post(`${API}/portal/restart-onu`, {}, { headers: getHeaders() });
      setRestartCooldownUntil(Date.now() + (60 * 1000));
      logPortalEvent('portal_onu_restart_requested');
      toast.success('Perintah restart ONU berhasil dikirim');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengirim restart ONU');
    } finally {
      setRestartingOnu(false);
    }
  };

  const handleCreateGatewayPayment = async (billingId) => {
    if (!billingId) return;
    setCreatingGatewayForBillingId(billingId);
    try {
      const response = await axios.post(`${API}/portal/payments/gateway/create`, {
        billing_id: billingId,
        provider: selectedGatewayProvider || 'xendit',
        channel: 'va_bca',
      }, { headers: getHeaders() });
      toast.success(response.data?.message || 'Virtual account berhasil dibuat');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal membuat pembayaran gateway');
    } finally {
      setCreatingGatewayForBillingId('');
    }
  };

  const handleSubmitTransferProof = async (billing) => {
    const billingId = billing?.id;
    if (!billingId) return;
    const bankId = selectedTransferBankByBilling[billingId] || paymentOptions.bank_accounts?.[0]?.id;
    const file = transferProofFileByBilling[billingId];
    const note = transferNoteByBilling[billingId] || '';
    if (!bankId) {
      toast.error('Pilih rekening tujuan dulu');
      return;
    }
    if (!file) {
      toast.error('Upload bukti transfer dulu');
      return;
    }
    setSubmittingTransferForBilling(billingId);
    try {
      const form = new FormData();
      form.append('billing_id', billingId);
      form.append('bank_account_id', bankId);
      form.append('note', note);
      form.append('proof_file', file);
      await axios.post(`${API}/portal/payments/transfer-submit`, form, { headers: getHeaders() });
      toast.success('Bukti transfer berhasil dikirim');
      setTransferProofFileByBilling((prev) => ({ ...prev, [billingId]: null }));
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal kirim bukti transfer');
    } finally {
      setSubmittingTransferForBilling('');
    }
  };
  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-100 text-green-400 border-green-200',
      unpaid: 'bg-yellow-100 text-yellow-400 border-yellow-200',
      overdue: 'bg-red-100 text-red-400 border-red-200'
    };
    const labels = {
      paid: 'Lunas',
      unpaid: 'Belum Bayar',
      overdue: 'Jatuh Tempo'
    };
    const icons = {
      paid: CheckCircle,
      unpaid: Clock,
      overdue: AlertCircle
    };
    const Icon = icons[status] || Clock;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.unpaid}`}>
        <Icon className="w-3.5 h-3.5" />
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] relative">
          <StaffPageLoadingOverlay text="Memuat portal pelanggan..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src={ktcLogo} 
              alt="KTC Logo" 
              className="h-10 w-auto"
            />
            <span className="font-outfit font-bold text-white hidden sm:block">Portal Pelanggan</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-white font-medium text-sm">{customer?.name}</p>
              <p className="text-gray-500 text-xs">{customer?.customer_id}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleLogout()}
              className="text-red-400 border-red-400/30 hover:bg-red-500/10"
              data-testid="portal-logout-button"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Welcome Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-brand-blue/20 to-brand-red/20 rounded-2xl p-6 mb-8 border border-slate-200"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="font-outfit text-2xl font-bold text-white">
                Selamat Datang, {customer?.name}!
              </h1>
              <p className="text-gray-400 mt-1">
                ID Pelanggan: {customer?.customer_id} | {customer?.package_name} ({customer?.package_speed})
              </p>
              {whatsAppChatLink?.url ? (
                <a
                  href={whatsAppChatLink.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex mt-3 items-center gap-2 rounded-lg bg-green-500 px-3 py-2 text-xs font-semibold text-white hover:bg-green-600"
                >
                  Chat Admin via WhatsApp
                </a>
              ) : null}
            </div>
            <div className={`px-4 py-2 rounded-xl ${
              customer?.status === 'active' 
                ? 'bg-green-100 text-green-400 border border-green-200' 
                : 'bg-red-100 text-red-400 border border-red-200'
            }`}>
              <div className="flex items-center gap-2">
                <Wifi className="w-5 h-5" />
                <span className="font-medium">
                  {customer?.status === 'active' ? 'Layanan Aktif' : 'Layanan Tidak Aktif'}
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-6">
          <Button
            variant={activeTab === 'overview' ? 'default' : 'outline'}
            onClick={() => setActiveTab('overview')}
            className={activeTab === 'overview' ? 'bg-brand-blue' : 'btn-secondary'}
          >
            <Home className="w-4 h-4 mr-2" />
            Home
          </Button>
          <Button
            variant={activeTab === 'billings' ? 'default' : 'outline'}
            onClick={() => setActiveTab('billings')}
            className={activeTab === 'billings' ? 'bg-brand-blue' : 'btn-secondary'}
          >
            <FileText className="w-4 h-4 mr-2" />
            Bills
          </Button>
          <Button
            variant={activeTab === 'profile' ? 'default' : 'outline'}
            onClick={() => setActiveTab('profile')}
            className={activeTab === 'profile' ? 'bg-brand-blue' : 'btn-secondary'}
          >
            <User className="w-4 h-4 mr-2" />
            Account
          </Button>
          <Button
            variant={activeTab === 'gangguan' ? 'default' : 'outline'}
            onClick={() => setActiveTab('gangguan')}
            className={activeTab === 'gangguan' ? 'bg-brand-blue' : 'btn-secondary'}
          >
            <Wrench className="w-4 h-4 mr-2" />
            Tickets
          </Button>
          <Button
            variant={activeTab === 'wifi' ? 'default' : 'outline'}
            onClick={() => setActiveTab('wifi')}
            className={activeTab === 'wifi' ? 'bg-brand-blue' : 'btn-secondary'}
          >
            <Wifi className="w-4 h-4 mr-2" />
            WiFi
          </Button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {/* Stats */}
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-5 border border-slate-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-brand-blue/20 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-brand-blue" />
                  </div>
                  <span className="text-gray-400 text-sm">Total Tagihan</span>
                </div>
                <p className="font-outfit text-2xl font-bold text-white">{summary?.total_invoices || 0}</p>
              </div>
              
              <div className="bg-white rounded-xl p-5 border border-slate-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  </div>
                  <span className="text-gray-400 text-sm">Sudah Dibayar</span>
                </div>
                <p className="font-outfit text-2xl font-bold text-green-400">{formatCurrency(summary?.total_paid)}</p>
              </div>
              
              <div className="bg-white rounded-xl p-5 border border-slate-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-yellow-500" />
                  </div>
                  <span className="text-gray-400 text-sm">Belum Dibayar</span>
                </div>
                <p className="font-outfit text-2xl font-bold text-yellow-400">{formatCurrency(summary?.total_unpaid)}</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-outfit text-lg font-semibold text-white">Status Koneksi</h2>
                <div className="flex items-center gap-2">
                  <span className="relative flex h-3 w-3">
                    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${healthConfig.color} opacity-75`}></span>
                    <span className={`relative inline-flex rounded-full h-3 w-3 ${healthConfig.color}`}></span>
                  </span>
                  <span className={`text-sm font-semibold ${healthConfig.textClass}`}>{healthConfig.text}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <div className="p-3 rounded-lg bg-white border border-slate-100">
                  <div className="flex items-center gap-1.5 mb-1"><Wifi className="w-3.5 h-3.5 text-slate-400" /><span className="text-gray-500 text-xs">ONU</span></div>
                  <p className={`font-semibold text-sm ${getOnuConnectionClass()}`}>{getOnuConnectionText()}</p>
                </div>
                <div className="p-3 rounded-lg bg-white border border-slate-100">
                  <div className="flex items-center gap-1.5 mb-1"><Activity className="w-3.5 h-3.5 text-slate-400" /><span className="text-gray-500 text-xs">PPPoE</span></div>
                  <p className={`font-semibold text-sm ${getPppoeStatusClass()}`}>{getPppoeStatusText()}</p>
                </div>
                <div className="p-3 rounded-lg bg-white border border-slate-100">
                  <div className="flex items-center gap-1.5 mb-1"><Signal className="w-3.5 h-3.5 text-slate-400" /><span className="text-gray-500 text-xs">Redaman</span></div>
                  <p className={`font-semibold text-sm ${getRedamanClass()}`}>{getRedamanText()}</p>
                </div>
                <div className="p-3 rounded-lg bg-white border border-slate-100">
                  <div className="flex items-center gap-1.5 mb-1"><ShieldCheck className="w-3.5 h-3.5 text-slate-400" /><span className="text-gray-500 text-xs">GenieACS</span></div>
                  <p className={`font-semibold text-sm ${getGenieacsClass()}`}>{getGenieacsText()}</p>
                </div>
                <div className="p-3 rounded-lg bg-white border border-slate-100">
                  <div className="flex items-center gap-1.5 mb-1"><ArrowUpDown className="w-3.5 h-3.5 text-slate-400" /><span className="text-gray-500 text-xs">Terhubung</span></div>
                  <p className="text-white font-semibold text-sm">{Number.isFinite(onuStatus?.total_stations) ? `${onuStatus.total_stations} perangkat` : '-'}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 border border-slate-200">
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-brand-blue" />
                  <h3 className="font-outfit text-base font-semibold text-white">Auto Diagnosis</h3>
                </div>
                <span className={`text-[11px] px-2 py-1 rounded border ${diagnosisConfig.chip}`}>{diagnosisConfig.label}</span>
              </div>
              <p className="text-sm text-gray-300">{autoDiagnosis.reason}</p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="font-outfit text-lg font-semibold text-white">Histori Redaman ONU</h2>
                  <p className="text-gray-500 text-sm">Grafik mengambil snapshot berkala dari status ONU yang tersimpan di server.</p>
                </div>
                <span className="text-[11px] px-2 py-1 rounded border border-cyan-500/20 bg-cyan-500/10 text-cyan-300">{statusHistory.length} titik</span>
              </div>
              <SignalHistoryChart history={statusHistory} />
            </div>

            {/* Recent Billings */}
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-outfit text-lg font-semibold text-white">Tagihan Terbaru</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActiveTab('billings')}
                  className="text-brand-blue"
                >
                  Lihat Semua
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              
              {billings.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Belum ada tagihan</p>
              ) : (
                <div className="space-y-3">
                  {sortedBillings.slice(0, 3).map((billing) => (
                    <div 
                      key={billing.id}
                      className="flex items-center justify-between p-4 rounded-lg bg-white border border-slate-100"
                    >
                      <div>
                        <p className="text-white font-medium">{billing.invoice_number}</p>
                        <p className="text-gray-500 text-sm">
                          {['', 'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'][billing.period_month]} {billing.period_year}
                        </p>
                      </div>
                      <div className="text-right flex items-center gap-4">
                        <div>
                          <p className="text-white font-semibold">{formatCurrency(billing.amount)}</p>
                          {getStatusBadge(billing.status)}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadPDF(billing.id, billing.invoice_number)}
                          className="text-gray-400 hover:text-white"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Account Info */}
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h2 className="font-outfit text-lg font-semibold text-white mb-4">Informasi Akun</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-white">
                  <p className="text-gray-500 text-sm">Username PPPoE</p>
                  <p className="text-white font-mono mt-1">{customer?.pppoe_username}</p>
                </div>
                <div className="p-4 rounded-lg bg-white">
                  <p className="text-gray-500 text-sm">Paket Layanan</p>
                  <p className="text-white mt-1">{customer?.package_name} - {customer?.package_speed}</p>
                </div>
                <div className="p-4 rounded-lg bg-white">
                  <p className="text-gray-500 text-sm">Biaya Bulanan</p>
                  <p className="text-white mt-1">{formatCurrency(customer?.package_price)}</p>
                </div>
                <div className="p-4 rounded-lg bg-white">
                  <p className="text-gray-500 text-sm">Bergabung Sejak</p>
                  <p className="text-white mt-1">{customer?.created_at ? formatDate(customer.created_at) : '-'}</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Billings Tab */}
        {activeTab === 'billings' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-xl p-6 border border-slate-200"
          >
            <h2 className="font-outfit text-xl font-semibold text-white mb-6">Riwayat Tagihan</h2>
            {latestBilling && (
              <div className="mb-5 rounded-xl border border-brand-blue/30 bg-brand-blue/10 p-4">
                <p className="text-xs uppercase tracking-wide text-brand-blue mb-1">Tagihan Terbaru</p>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <p className="text-sm text-white font-semibold">{latestBilling.invoice_number}</p>
                    <p className="text-xs text-gray-400">Jatuh tempo: {formatDate(latestBilling.due_date)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(latestBilling.status)}
                    <span className="text-sm font-semibold text-white">{formatCurrency(latestBilling.amount)}</span>
                  </div>
                </div>
              </div>
            )}
            
            {sortedBillings.length === 0 ? (
              <p className="text-gray-500 text-center py-12">Belum ada tagihan</p>
            ) : (
              <div className="space-y-4">
                {sortedBillings.map((billing) => (
                  <div 
                    key={billing.id}
                    className="p-5 rounded-xl bg-white border border-slate-100"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <p className="text-white font-semibold">{billing.invoice_number}</p>
                          {getStatusBadge(billing.status)}
                        </div>
                        <p className="text-gray-400 text-sm">
                          Periode: {['', 'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'][billing.period_month]} {billing.period_year}
                        </p>
                        <p className="text-gray-500 text-sm">
                          Jatuh Tempo: {formatDate(billing.due_date)}
                        </p>
                        {billing.status === 'paid' && billing.payment_date && (
                          <p className="text-green-400 text-sm">
                            Dibayar: {formatDate(billing.payment_date)}
                          </p>
                        )}
                        {billing.payment_method && (
                          <p className="text-cyan-300 text-xs">
                            Metode: {String(billing.payment_method).toUpperCase()}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-4">
                        <p className="text-white text-xl font-bold">{formatCurrency(billing.amount)}</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadPDF(billing.id, billing.invoice_number)}
                          className="btn-secondary"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          PDF
                        </Button>
                      </div>
                    </div>
                    {billing.status !== 'paid' && (
                      <div className="mt-4 rounded-lg border border-slate-200 bg-[#111111] p-4 space-y-3">
                        <p className="text-sm text-white font-semibold">Metode Pembayaran</p>
                        {paymentOptions.transfer_enabled && (
                          <div className="space-y-2">
                            <p className="text-xs text-gray-400">Transfer Manual (rekening admin)</p>
                            {paymentOptions.bank_accounts.length === 0 ? (
                              <p className="text-xs text-yellow-300">Rekening belum diatur admin.</p>
                            ) : (
                              <div className="space-y-2">
                                <div className="grid md:grid-cols-2 gap-2">
                                  {paymentOptions.bank_accounts.map((acc) => (
                                    <div key={acc.id} className="rounded border border-slate-200 bg-white p-2">
                                      <p className="text-xs text-white font-medium">{acc.bank_name} - {acc.account_number}</p>
                                      <p className="text-[11px] text-gray-400">{acc.account_name}</p>
                                      {acc.note ? <p className="text-[11px] text-gray-500">{acc.note}</p> : null}
                                    </div>
                                  ))}
                                </div>
                                <div className="grid md:grid-cols-3 gap-2">
                                  <select
                                    value={selectedTransferBankByBilling[billing.id] || paymentOptions.bank_accounts?.[0]?.id || ''}
                                    onChange={(e) => setSelectedTransferBankByBilling((prev) => ({ ...prev, [billing.id]: e.target.value }))}
                                    className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-white text-sm"
                                  >
                                    {paymentOptions.bank_accounts.map((acc) => (
                                      <option key={acc.id} value={acc.id}>{acc.bank_name} - {acc.account_number}</option>
                                    ))}
                                  </select>
                                  <input
                                    type="text"
                                    value={transferNoteByBilling[billing.id] || ''}
                                    onChange={(e) => setTransferNoteByBilling((prev) => ({ ...prev, [billing.id]: e.target.value }))}
                                    className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-white text-sm"
                                    placeholder="Catatan transfer (opsional)"
                                  />
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => setTransferProofFileByBilling((prev) => ({ ...prev, [billing.id]: e.target.files?.[0] || null }))}
                                    className="px-2 py-2 bg-white border border-slate-200 rounded-lg text-white text-xs"
                                  />
                                </div>
                                <div className="flex justify-end">
                                  <Button
                                    size="sm"
                                    className="bg-green-600 hover:bg-green-700 text-white"
                                    onClick={() => handleSubmitTransferProof(billing)}
                                    disabled={submittingTransferForBilling === billing.id}
                                  >
                                    {submittingTransferForBilling === billing.id ? 'Mengirim...' : 'Kirim Bukti Transfer'}
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {paymentOptions.gateway_enabled && (
                          <div className="space-y-2">
                            <p className="text-xs text-gray-400">Payment Gateway</p>
                            <div className="flex flex-wrap items-center gap-2">
                              <select
                                value={selectedGatewayProvider}
                                onChange={(e) => setSelectedGatewayProvider(e.target.value)}
                                className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-white text-sm"
                              >
                                {(paymentOptions.providers || []).map((provider) => (
                                  <option key={provider} value={provider}>{provider.toUpperCase()}</option>
                                ))}
                              </select>
                              <Button
                                size="sm"
                                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                                onClick={() => handleCreateGatewayPayment(billing.id)}
                                disabled={creatingGatewayForBillingId === billing.id || !selectedGatewayProvider}
                              >
                                {creatingGatewayForBillingId === billing.id ? 'Membuat VA...' : 'Buat Virtual Account'}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-white font-semibold mb-3">Riwayat Virtual Account</p>
              {gatewayTransactions.length === 0 ? (
                <p className="text-sm text-gray-500">Belum ada transaksi gateway.</p>
              ) : (
                <div className="space-y-2">
                  {gatewayTransactions.map((tx) => (
                    <div key={tx.id} className="rounded border border-slate-200 bg-white p-3">
                      <p className="text-sm text-white">{tx.invoice_number} • {tx.provider?.toUpperCase()} • {tx.status?.toUpperCase()}</p>
                      <p className="text-xs text-gray-400">VA: {tx.virtual_account_number} • Exp: {tx.expires_at ? formatDate(tx.expires_at) : '-'}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-white font-semibold mb-3">Riwayat Bukti Transfer</p>
              {transferSubmissions.length === 0 ? (
                <p className="text-sm text-gray-500">Belum ada bukti transfer.</p>
              ) : (
                <div className="space-y-2">
                  {transferSubmissions.map((item) => (
                    <div key={item.id} className="rounded border border-slate-200 bg-white p-3">
                      <p className="text-sm text-white">{item.invoice_number} • {item.status?.toUpperCase()}</p>
                      <p className="text-xs text-gray-400">{item.bank_account_name} - {item.bank_account_number}</p>
                      {item.proof_file_url ? (
                        <a
                          href={`${BASE_URL}${item.proof_file_url}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-brand-blue hover:underline"
                        >
                          Lihat bukti transfer
                        </a>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {activeTab === 'gangguan' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h2 className="font-outfit text-xl font-semibold text-white mb-4">Buka Tiket Gangguan</h2>
              <form onSubmit={handleCreateTicket} className="space-y-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Kategori Masalah</label>
                  <select
                    value={ticketForm.category}
                    onChange={(e) => setTicketForm((prev) => ({ ...prev, category: e.target.value }))}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-white focus:border-brand-blue focus:outline-none"
                  >
                    <option value="internet_lambat">🐌 Internet Lambat</option>
                    <option value="putus_total">🔴 Putus Total</option>
                    <option value="tidak_stabil">📶 Koneksi Tidak Stabil</option>
                    <option value="onu_mati">💡 ONU Mati / Lampu Merah</option>
                    <option value="wifi_bermasalah">📡 WiFi Bermasalah</option>
                    <option value="lainnya">📝 Lainnya</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Keluhan</label>
                  <textarea
                    value={ticketForm.issue_notes}
                    onChange={(event) => setTicketForm((prev) => ({ ...prev, issue_notes: event.target.value }))}
                    rows={4}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-white focus:border-brand-blue focus:outline-none resize-none"
                    placeholder="Contoh: Internet sering putus sejak tadi pagi"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Foto / Screenshot (opsional, maks 3)</label>
                  <label className="flex items-center gap-2 cursor-pointer px-4 py-3 bg-white border border-dashed border-slate-300 rounded-lg text-gray-400 hover:border-brand-blue hover:text-brand-blue transition-colors">
                    <Camera className="w-4 h-4" />
                    <span className="text-sm">{ticketPhotos.length > 0 ? `${ticketPhotos.length} foto dipilih` : 'Pilih foto...'}</span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []).slice(0, 3);
                        setTicketPhotos(files);
                      }}
                    />
                  </label>
                  {ticketPhotos.length > 0 && (
                    <div className="flex gap-2 mt-2">
                      {ticketPhotos.map((file, i) => (
                        <span key={i} className="text-xs px-2 py-1 rounded bg-slate-100 text-gray-500 border border-slate-200">{file.name}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex justify-end">
                  <Button type="submit" className="bg-brand-blue hover:bg-brand-blue/90" disabled={ticketSubmitting}>
                    <Upload className="w-4 h-4 mr-2" />
                    {ticketSubmitting ? 'Menyimpan tiket...' : 'Kirim Tiket'}
                  </Button>
                </div>
              </form>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h2 className="font-outfit text-xl font-semibold text-white mb-4">Riwayat Tiket Gangguan</h2>
              {tickets.length === 0 ? (
                <p className="text-gray-500">Belum ada tiket gangguan.</p>
              ) : (
                <div className="space-y-3">
                  {tickets.map((ticket) => (
                    <div key={ticket.id} className="rounded-lg border border-slate-200 bg-white p-4 space-y-2">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <p className="text-white font-medium">{ticket.customer_name || customer?.name || '-'}</p>
                        <p className={`text-sm font-semibold ${getTicketStatusClass(ticket.status)}`}>
                          {getTicketStatusLabel(ticket.status)}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500">Dibuat: {ticket.created_at ? formatDate(ticket.created_at) : '-'}</p>
                      <p className="text-sm text-gray-300">Keluhan: {ticket.issue_notes || '-'}</p>
                      <p className="text-xs text-gray-500">Teknisi: {(ticket.approved_technician_names || []).join(', ') || '-'}</p>
                      {ticket.completion_notes && (
                        <p className="text-xs text-gray-400">Catatan penyelesaian: {ticket.completion_notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <CustomerProfile customer={customer} onUpdate={fetchData} onLogout={handleLogout} />
        )}

        {activeTab === 'wifi' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white rounded-xl p-6 border border-slate-200"
          >
            <h2 className="font-outfit text-xl font-semibold text-white mb-6">Pengaturan WiFi ONU</h2>

            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <div className="p-4 rounded-lg bg-white border border-slate-100">
                <p className="text-gray-500 text-sm">Status ONU</p>
                <p className={`font-medium mt-1 ${getOnuConnectionClass()}`}>{getOnuConnectionText()}</p>
                <p className={`text-sm mt-2 ${getRedamanClass()}`}>{getRedamanText()}</p>
              </div>
              <div className="p-4 rounded-lg bg-white border border-slate-100">
                <p className="text-gray-500 text-sm">Status ACS</p>
                <p className={`font-medium mt-1 ${getGenieacsClass()}`}>{getGenieacsText()}</p>
                <p className="text-xs text-gray-500 mt-2">
                  Sinkron terakhir: {wifiReadiness.lastSyncAt ? formatDate(wifiReadiness.lastSyncAt) : 'Belum ada data'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Terakhir terlihat: {wifiReadiness.lastSeenAt ? formatDate(wifiReadiness.lastSeenAt) : 'Belum ada data'}
                </p>
              </div>
              <div className="p-4 rounded-lg bg-white border border-slate-100">
                <p className="text-gray-500 text-sm">Perangkat yang Terhubung</p>
                <p className="text-white font-medium mt-1">
                  {Number.isFinite(onuStatus?.total_stations) ? onuStatus.total_stations : '-'}
                </p>
                <p className="text-xs text-gray-500 mt-2">Flow aman: cek kesiapan > konfirmasi > simpan > cek hasil</p>
              </div>
            </div>

            {!wifiReadiness.ready ? (
              <div className="mb-5 rounded-lg border border-yellow-400/30 bg-yellow-500/10 p-4">
                <p className="text-sm font-semibold text-yellow-300">Perubahan WiFi sementara dinonaktifkan</p>
                <ul className="mt-2 space-y-1 text-xs text-yellow-200">
                  {wifiReadiness.reasons.map((reason) => (
                    <li key={reason}>- {reason}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {wifiRequestStatus.state !== 'idle' ? (
              <div className={`mb-5 rounded-lg border p-3 ${
                wifiRequestStatus.state === 'success'
                  ? 'border-green-500/30 bg-green-500/10'
                  : wifiRequestStatus.state === 'failed'
                    ? 'border-red-500/30 bg-red-500/10'
                    : 'border-cyan-500/30 bg-cyan-500/10'
              }`}>
                <p className={`text-sm font-medium ${
                  wifiRequestStatus.state === 'success'
                    ? 'text-green-300'
                    : wifiRequestStatus.state === 'failed'
                      ? 'text-red-300'
                      : 'text-cyan-300'
                }`}>
                  {wifiRequestStatus.state === 'success'
                    ? 'Berhasil'
                    : wifiRequestStatus.state === 'failed'
                      ? 'Gagal'
                      : wifiRequestStatus.state === 'queued'
                        ? 'Dalam Antrean'
                        : wifiRequestStatus.state === 'pending_confirmation'
                          ? 'Menunggu Konfirmasi'
                          : 'Sedang Diproses'}
                </p>
                <p className="text-xs text-gray-300 mt-1">{wifiRequestStatus.message || 'Menunggu status terbaru...'}</p>
                {wifiRequestStatus.requestId ? (
                  <p className="text-[11px] text-gray-500 mt-1">Request ID: {wifiRequestStatus.requestId}</p>
                ) : null}
              </div>
            ) : null}

            <div className="space-y-5">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Nama WiFi (SSID)</label>
                <input
                  type="text"
                  value={wifiForm.ssid_name}
                  onChange={(e) => {
                    setWifiForm((prev) => ({ ...prev, ssid_name: e.target.value }));
                    setWifiErrors((prev) => ({ ...prev, ssid_name: '' }));
                  }}
                  disabled={!wifiReadiness.ready || wifiSaving || wifiPolling}
                  className={`w-full px-4 py-3 bg-white border rounded-lg text-white focus:border-brand-blue focus:outline-none ${
                    wifiErrors.ssid_name ? 'border-red-500/60' : 'border-slate-200'
                  }`}
                  placeholder="Contoh: KTC-Rumah-Ahmad"
                />
                {wifiErrors.ssid_name ? <p className="text-xs text-red-300 mt-1">{wifiErrors.ssid_name}</p> : null}
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Password WiFi</label>
                <div className="relative">
                  <input
                    type={showWifiPassword ? 'text' : 'password'}
                    value={wifiForm.ssid_password}
                    onChange={(e) => {
                      setWifiForm((prev) => ({ ...prev, ssid_password: e.target.value }));
                      setWifiErrors((prev) => ({ ...prev, ssid_password: '' }));
                    }}
                    disabled={!wifiReadiness.ready || wifiSaving || wifiPolling}
                    className={`w-full px-4 py-3 pr-12 bg-white border rounded-lg text-white focus:border-brand-blue focus:outline-none ${
                      wifiErrors.ssid_password ? 'border-red-500/60' : 'border-slate-200'
                    }`}
                    placeholder="Minimal 8 karakter"
                  />
                  <button
                    type="button"
                    onClick={() => setShowWifiPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                    disabled={!wifiReadiness.ready || wifiSaving || wifiPolling}
                  >
                    {showWifiPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {wifiErrors.ssid_password ? <p className="text-xs text-red-300 mt-1">{wifiErrors.ssid_password}</p> : null}
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Konfirmasi Password WiFi</label>
                <input
                  type={showWifiPassword ? 'text' : 'password'}
                  value={wifiForm.confirm_password}
                  onChange={(e) => {
                    setWifiForm((prev) => ({ ...prev, confirm_password: e.target.value }));
                    setWifiErrors((prev) => ({ ...prev, confirm_password: '' }));
                  }}
                  disabled={!wifiReadiness.ready || wifiSaving || wifiPolling}
                  className={`w-full px-4 py-3 bg-white border rounded-lg text-white focus:border-brand-blue focus:outline-none ${
                    wifiErrors.confirm_password ? 'border-red-500/60' : 'border-slate-200'
                  }`}
                  placeholder="Ulangi password WiFi"
                />
                {wifiErrors.confirm_password ? <p className="text-xs text-red-300 mt-1">{wifiErrors.confirm_password}</p> : null}
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  onClick={() => setRestartConfirmOpen(true)}
                  disabled={restartingOnu || !wifiReadiness.ready || restartCooldownLeft > 0}
                  variant="outline"
                  className="btn-secondary"
                >
                  <RotateCcw className={`w-4 h-4 mr-2 ${restartingOnu ? 'animate-spin' : ''}`} />
                  {restartCooldownLeft > 0 ? `Cooldown ${restartCooldownLeft}s` : restartingOnu ? 'Mengirim restart...' : 'Restart ONU'}
                </Button>
                <Button
                  onClick={() => {
                    const { valid } = validateWifiForm();
                    if (!valid) return;
                    setWifiConfirmOpen(true);
                  }}
                  disabled={wifiSaving || wifiPolling || !wifiReadiness.ready}
                  className="bg-brand-blue hover:bg-brand-blue/90"
                >
                  {wifiSaving ? 'Menyimpan...' : wifiPolling ? 'Menunggu hasil...' : 'Simpan Pengaturan WiFi'}
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        <Dialog open={wifiConfirmOpen} onOpenChange={setWifiConfirmOpen}>
          <DialogContent className="bg-[#101010] border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle>Konfirmasi Perubahan WiFi</DialogTitle>
              <DialogDescription className="text-gray-300">
                Setelah disimpan, semua perangkat akan terputus dan harus terhubung ulang dengan SSID/password baru.
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-md border border-yellow-500/30 bg-yellow-500/10 p-3 text-xs text-yellow-200">
              Pastikan Anda mengingat password baru sebelum melanjutkan.
            </div>
            <DialogFooter>
              <Button variant="outline" className="btn-secondary" onClick={() => setWifiConfirmOpen(false)}>
                Batal
              </Button>
              <Button className="bg-brand-blue hover:bg-brand-blue/90" onClick={() => void handleSaveWifi()} disabled={wifiSaving}>
                Lanjut Simpan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={restartConfirmOpen} onOpenChange={setRestartConfirmOpen}>
          <DialogContent className="bg-[#101010] border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle>Konfirmasi Restart ONU</DialogTitle>
              <DialogDescription className="text-gray-300">
                Koneksi internet akan putus sementara selama proses restart perangkat ONU.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" className="btn-secondary" onClick={() => setRestartConfirmOpen(false)}>
                Batal
              </Button>
              <Button
                className="bg-orange-600 hover:bg-orange-700 text-white"
                onClick={() => void handleRestartOnu()}
                disabled={restartingOnu || restartCooldownLeft > 0}
              >
                {restartingOnu ? 'Mengirim...' : 'Ya, Restart ONU'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

// Profile Component
const CustomerProfile = ({ customer, onUpdate, onLogout }) => {
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: customer?.name || '',
    phone: customer?.phone || '',
    email: customer?.email || '',
    address: customer?.address || ''
  });
  const [passwordForm, setPasswordForm] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    setFormData({
      name: customer?.name || '',
      phone: customer?.phone || '',
      email: customer?.email || '',
      address: customer?.address || ''
    });
  }, [customer?.name, customer?.phone, customer?.email, customer?.address]);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('ktc_customer_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        address: formData.address
      };
      await axios.put(`${API}/portal/profile`, payload, { headers: getHeaders() });

      const phoneChanged = (customer?.phone || '') !== (formData.phone || '');
      if (phoneChanged) {
        toast.success('Nomor HP berhasil diubah. Silakan login ulang.');
        onLogout();
        return;
      }

      toast.success('Profil berhasil diperbarui');
      setEditing(false);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memperbarui profil');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.old_password || !passwordForm.new_password || !passwordForm.confirm_password) {
      toast.error('Lengkapi semua field password');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('Password baru minimal 6 karakter');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Konfirmasi password tidak sama');
      return;
    }

    setPasswordLoading(true);
    try {
      await axios.put(`${API}/portal/change-password`, {
        old_password: passwordForm.old_password,
        new_password: passwordForm.new_password
      }, { headers: getHeaders() });

      toast.success('Password berhasil diubah. Silakan login ulang.');
      onLogout();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengubah password');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-white rounded-xl p-6 border border-slate-200"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-outfit text-xl font-semibold text-white">Profil Saya</h2>
        {!editing ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditing(true)}
            className="btn-secondary"
          >
            <Settings className="w-4 h-4 mr-2" />
            Edit Profil
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(false)}
              className="btn-secondary"
            >
              Batal
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={loading}
              className="bg-brand-blue hover:bg-brand-blue/90"
            >
              {loading ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-gray-400 text-sm mb-2">Nama Lengkap</label>
          {editing ? (
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-white focus:border-brand-blue focus:outline-none"
            />
          ) : (
            <p className="text-white py-3">{customer?.name}</p>
          )}
        </div>

        <div>
          <label className="block text-gray-400 text-sm mb-2">No. Telepon</label>
          {editing ? (
            <input
              type="text"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-white focus:border-brand-blue focus:outline-none"
            />
          ) : (
            <p className="text-white py-3">{customer?.phone}</p>
          )}
        </div>

        <div>
          <label className="block text-gray-400 text-sm mb-2">Email</label>
          {editing ? (
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-white focus:border-brand-blue focus:outline-none"
              placeholder="email@example.com"
            />
          ) : (
            <p className="text-white py-3">{customer?.email || '-'}</p>
          )}
        </div>

        <div>
          <label className="block text-gray-400 text-sm mb-2">Alamat</label>
          {editing ? (
            <textarea
              value={formData.address}
              onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-white focus:border-brand-blue focus:outline-none resize-none"
              rows={3}
            />
          ) : (
            <p className="text-white py-3">{customer?.address}</p>
          )}
        </div>

        <div>
          <label className="block text-gray-400 text-sm mb-2">ID Pelanggan</label>
          <p className="text-white py-3 font-mono">{customer?.customer_id}</p>
        </div>

        <div>
          <label className="block text-gray-400 text-sm mb-2">Username PPPoE</label>
          <p className="text-white py-3 font-mono">{customer?.pppoe_username}</p>
        </div>
      </div>

      <div className="mt-8 pt-6 border-t border-slate-200">
        <h3 className="font-outfit text-lg font-semibold text-white mb-4">Ganti Password Login</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-gray-400 text-sm mb-2">Password Lama</label>
            <input
              type="password"
              value={passwordForm.old_password}
              onChange={(e) => setPasswordForm((prev) => ({ ...prev, old_password: e.target.value }))}
              className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-white focus:border-brand-blue focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">Password Baru</label>
            <input
              type="password"
              value={passwordForm.new_password}
              onChange={(e) => setPasswordForm((prev) => ({ ...prev, new_password: e.target.value }))}
              className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-white focus:border-brand-blue focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">Konfirmasi Password Baru</label>
            <input
              type="password"
              value={passwordForm.confirm_password}
              onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirm_password: e.target.value }))}
              className="w-full px-4 py-3 bg-white border border-slate-200 rounded-lg text-white focus:border-brand-blue focus:outline-none"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button
            onClick={handleChangePassword}
            disabled={passwordLoading}
            className="bg-brand-blue hover:bg-brand-blue/90"
          >
            {passwordLoading ? 'Menyimpan...' : 'Ubah Password'}
          </Button>
        </div>
      </div>
    </motion.div>
  );
};
export default CustomerPortal;







































